import { COLUMN_DEFINITIONS } from '@/src/components/Settings/Sections/UsersTable/users-config';
import { useAccountApi } from '@/src/hooks/api/useAccountApi';
import { CognitoUser } from '@/src/types/User';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { preProdLogger } from '@/src/utils/log.utils';
import { paginationLabels } from '@/src/utils/table.utils';
import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  Box,
  Button,
  CollectionPreferencesProps,
  Pagination,
  SpaceBetween,
  Spinner,
  Table,
  TextFilter,
} from '@amzn/awsui-components-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableHeader } from '../../../common/TableHeader';

export const UsersTable: React.FC<{ onUsersSelect: (users: CognitoUser[] | null | undefined) => void }> = ({
  onUsersSelect,
}) => {
  const { t } = useTranslation();
  const [preferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: 50,
  });
  const [users, setUsers] = useState<CognitoUser[]>([]);
  const [loading, setLoading] = useState(false);
  const accountApi = useAccountApi();

  const { items, collectionProps, paginationProps, filterProps } = useCollection(users, {
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
    filtering: {
      filteringFunction: (item: CognitoUser, filteringText: string) =>
        !!item?.email && item.email.includes(filteringText),
      noMatch: t(i18nKeys.settings.userManagement.table.noUsersFound),
      empty: loading ? t(i18nKeys.general.loading) : t(i18nKeys.settings.userManagement.table.emptyState),
    },
  });

  const loadUsers = async (includeGroups = false) => {
    if (!!users.length) return;
    setLoading(true);

    try {
      let nextToken: string | undefined;

      do {
        const response = await accountApi.listUsers({ ...(!nextToken ? {} : { nextToken }), includeGroups });

        nextToken = response?.nextToken;
        setUsers((prev) => prev.concat(response?.userResponses || []));
      } while (nextToken);
    } catch (e: any) {
      preProdLogger('Error loading users', e.message);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (!!filterProps.filteringText && !loading && !users?.length) {
      loadUsers();
    }
  }, [filterProps.filteringText, loading]);

  return (
    <Table
      {...collectionProps}
      header={
        <Box margin={{ vertical: 's' }}>
          <TableHeader
            title="Users"
            actionButtons={
              <Box float="right">
                <Button iconName="refresh" onClick={() => void loadUsers()}>
                  {t(i18nKeys.general.refresh)}
                </Button>
              </Box>
            }
          />
        </Box>
      }
      pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels(t)} />}
      items={items}
      resizableColumns
      wrapLines
      columnDefinitions={COLUMN_DEFINITIONS({
        setSelectedUser: (user: CognitoUser) => {
          onUsersSelect([user]);
        },
      })}
      data-testid="users-table"
      filter={
        <SpaceBetween direction="horizontal" size="s">
          <TextFilter
            {...filterProps}
            data-testid="users_table__text-filter"
            filteringPlaceholder={t(i18nKeys.usagePlan.fields.allowedUsers.placeholder)}
            loading={loading}
          />
          {loading && <Spinner />}
        </SpaceBetween>
      }
    />
  );
};
export default UsersTable;
